/**
 * Sélection des alertes de fin de mandat CAC (R-CAC-02 : échéances à 90 / 60 / 30 jours).
 * Fonctions pures, sans dépendance Angular.
 *
 * Partagées par la modale d'alertes et par le décompte porté par le bouton : dupliquer ce
 * prédicat ferait diverger le nombre annoncé et la liste effectivement ouverte.
 */

import { joursAvant, nomSuppleant, toRattachement } from './cac-api.mappers';
import type { MandatCommissaireAuxComptes } from './models/cac-api.model';
import type { AlerteMandat, RattachementCac } from './models/cac.model';

/**
 * Alertes des mandats échéant dans les 90 jours suivant `today`, les plus proches d'abord.
 *
 * Un mandat porte la paire entière depuis la refonte d'octobre 2026 : le suppléant se lit dans le
 * mandat qui expire, sans second chargement.
 */
export function construireAlertes(mandats: MandatCommissaireAuxComptes[], today: Date): AlerteMandat[] {
    const alertes: AlerteMandat[] = [];
    for (const m of mandats) {
        const jours = joursAvant(m.date_fin, today);
        if (jours < 0 || jours > 90) {
            continue;
        }
        const qualite: AlerteMandat['qualite_alerte'] = jours <= 30 ? '30J' : jours <= 60 ? '60J' : '90J';
        alertes.push({
            rattachement: toRattachement(m),
            jours_avant_expiration: jours,
            qualite_alerte: qualite,
            suppleant_nom: nomSuppleant(m),
        });
    }
    return alertes.sort((a, b) => a.jours_avant_expiration - b.jours_avant_expiration);
}

/**
 * Alertes rattachées à un rattachement donné ; sans contexte, l'ensemble du portefeuille.
 * L'identité tient à deux clés : la société et le commissaire titulaire — la qualité a disparu
 * du contrat, le mandat portant les deux rôles.
 */
export function alertesDuRattachement(alertes: AlerteMandat[], contexte: RattachementCac | null | undefined): AlerteMandat[] {
    if (!contexte) {
        return alertes;
    }
    return alertes.filter(
        (a) => a.rattachement.societe_id === contexte.societe_id && a.rattachement.commissaire_aux_comptes_id === contexte.commissaire_aux_comptes_id,
    );
}

/** Libellé du suppléant d'une alerte ; l'acte peut n'en avoir désigné aucun. */
export function libelleSuppleantAlerte(alerte: AlerteMandat): string {
    return alerte.suppleant_nom ?? 'Aucun suppléant désigné';
}

/** Une alerte devient grave au dernier palier de R-CAC-02 : 30 jours avant l'échéance. */
export function aAlerteMandatCritique(alertes: AlerteMandat[]): boolean {
    return alertes.some((a) => a.qualite_alerte === '30J');
}
